
import WDNavbarMenu from './wd-navbar-menu';

export default class WDNavbarMenuPager extends WDNavbarMenu
{
	#page = 0;
	#count = 0;
	#max = -1;
	#pages = [];
	#previous = null;
	#next = null;
	#config = {
		compact: false,
		showPrevNext: true,
		maxVisible: 7 // Default number of visible page buttons
	};

	constructor(cfg)
	{
		super(cfg).classList.add('wd-navbar-pager');
		return this;
	};

	// Enhanced load method with responsive options
	load(opts) 
	{
		// Store configuration options
		Object.assign(this.#config, opts);
		
		this.#pages = [];

		// Calculate number of pages
		this.#count = Math.ceil(parseFloat(opts.count) / parseFloat(opts.limit));

		if (opts.max_pages) this.#max = opts.max_pages;

		this.render();
	};

	get isLastPage()
	{
		return (parseInt(this.#page) === parseInt(this.#count) - 1);
	}

	get isFirstPage()
	{
		return ( parseInt(this.#page) === 0);
	}

	render()
	{
		this.empty();
		this.setupEndpoints();

		// Previous page button
		if (this.#config.showPrevNext) {
			this.#previous = this.add({ 
				fasIcon: 'chevron-left',
				title: 'Previous page',
				click: (evt) => this.previousPage(evt) 
			});
			this.#previous.disabled = (this.#page === 0);
			this.#previous.classList.add('nav-button', 'prev-button');
		}

		// Render page numbers with ellipsis
		this.renderPageNumbers();

		// Next page button
		if (this.#config.showPrevNext) {
			this.#next = this.add({ 
				fasIcon: 'chevron-right',
				title: 'Next page',
				click: (evt) => this.nextPage(evt)
			});
			this.#next.disabled = (this.#page === this.#count - 1);
			this.#next.classList.add('nav-button', 'next-button');
		}

		// Apply compact styling if needed
		if (this.#config.compact) {
			this.classList.add('compact');
		}
		else
		{
			this.classList.remove('compact');
		}

		if (this.#pages.length > 0) this.updateActive();
	}

	renderPageNumbers()
	{
		const current = this.#page;
		const total = this.#count;
		const maxVisible = this.#max;

		// If we can show all pages, show them
		if (total <= maxVisible) {
			for (let i = 0; i < total; i++) {
				this.addPageButton(i);
			}
			return;
		}

		// Always show first page
		this.addPageButton(0);

		// Determine ellipsis placement based on current position
		const showLeftEllipsis = current > 3;
		const showRightEllipsis = current < total - 4;

		if (showLeftEllipsis) {
			this.addEllipsis('start');
		}

		// Calculate the range of pages around current page
		let startRange, endRange;

		if (current <= 3) {
			// Near beginning: show pages 1,2,3,4... ellipsis ... last
			startRange = 1;
			endRange = Math.min(maxVisible - 2, total - 2);
		} else if (current >= total - 4) {
			// Near end: show first ... ellipsis ... N-3,N-2,N-1,N
			startRange = Math.max(1, total - maxVisible + 1);
			endRange = total - 1;
		} else {
			// In middle: show first ... current-1, current, current+1 ... last
			const sidePages = Math.floor((maxVisible - 4) / 2); // -4 for first, last, and 2 ellipses
			startRange = Math.max(1, current - sidePages);
			endRange = Math.min(total - 1, current + sidePages);
		}

		// Add the calculated range of pages
		for (let i = startRange; i <= endRange; i++) {
			this.addPageButton(i);
		}

		if (showRightEllipsis) {
			this.addEllipsis('end');
		}

		// Always show last page (if it's not already shown)
		if (total > 1 && endRange < total - 1) {
			this.addPageButton(total - 1);
		}
	}

	addPageButton(pageIndex)
	{
		const btn = this.add(String(pageIndex + 1));
		btn.setAttribute('data-page', pageIndex);
		btn.on('click', () => this.select(pageIndex));
		btn.classList.add('page-button');
		
		if (pageIndex === this.#page) {
			btn.classList.add('active');
		}
		
		this.#pages.push(btn);
		return btn;
	}

	addEllipsis(position)
	{
		const ellipsis = this.add({ 
			label: '…', 
			disabled: true,
			click: (evt) => this.handleEllipsisClick(evt, position)
		});
		ellipsis.classList.add('ellipsis', `ellipsis-${position}`);
		ellipsis.disabled = false; // Make clickable for better UX
		return ellipsis;
	}

	handleEllipsisClick(evt, position)
	{
		const jumpSize = Math.max(1, Math.floor(this.#max / 2));
		
		if (position === 'start') {
			// Jump backward
			const newPage = Math.max(0, this.#page - jumpSize);
			this.select(newPage);
		} else if (position === 'end') {
			// Jump forward
			const newPage = Math.min(this.#count - 1, this.#page + jumpSize);
			this.select(newPage);
		}
	}

	setupEndpoints()
	{
		// This method is simplified since we now handle page ranges in renderPageNumbers
		if (this.#max == -1) {
			this.#max = this.#config.maxVisible;
		}
	}

	get page() { return this.#page; }
	set page(idx) {
		this.#page = parseInt(idx) || 0;
		this.render(); // Redraw to update ellipsis positioning
	}

	select(idx) 
	{
		const newPage = parseInt(idx);
		if (newPage === this.#page) return; // No change needed
		
		this.#page = newPage;
		this.render(); // Redraw to update ellipsis positioning
		this.trigger('page:selected', { page: this.#page });
	}

	updateActive()
	{
		// Remove current active state
		const activePage = this.querySelector('.current-page');
		if (activePage) activePage.classList.remove('current-page');

		// Set new active state  
		const currentPageBtn = this.querySelector(`[data-page="${this.#page}"]`);
		if (currentPageBtn) {
			currentPageBtn.classList.add('current-page');
		}

		// Update button states
		this.updateButtonStates();
	}

	updateButtonStates()
	{
		// Update navigation buttons
		if (this.#previous) this.#previous.disabled = this.isFirstPage;
		if (this.#next) this.#next.disabled = this.isLastPage;

		// Update page status classes
		this.classList.toggle('is-first-page', this.isFirstPage);
		this.classList.toggle('is-last-page', this.isLastPage);
	}

	nextPage(evt)
	{
		evt?.preventDefault();
		if (this.#page < this.#count - 1) {
			this.select(this.#page + 1);
		}
	}

	previousPage(evt)
	{
		evt?.preventDefault();
		if (this.#page > 0) {
			this.select(this.#page - 1);
		}
	}

	// Utility methods
	getTotalPages()
	{
		return this.#count;
	}

	hasNextPage()
	{
		return this.#page < this.#count - 1;
	}

	hasPreviousPage()
	{
		return this.#page > 0;
	}

	// Method to update responsive settings without full reload
	updateResponsiveMode(isCompact, maxVisible = null)
	{
		this.#config.compact = isCompact;
		if (maxVisible) {
			this.#config.maxVisible = maxVisible;
			this.#max = maxVisible;
		}
		
		this.classList.toggle('compact', isCompact);
		this.render();
	}
}

customElements.define('wd-navbar-menu-pager', WDNavbarMenuPager);
